export function flatMap<T, Ret>(
  arr: T[],
  func: (item: T, i: number, arr: T[]) => Ret | Ret[],
): Ret[] {
  let retValue: Ret[] = []

  arr.forEach((el, i) => {
    const elValue = func(el, i, arr)
    if (Array.isArray(elValue)) {
      retValue = retValue.concat(elValue)
    } else {
      retValue = retValue.concat([elValue])
    }
  })
  return retValue
}

export function unique<T>(arr: T[]): T[] {
  return arr.filter((el, i, arr) => arr.indexOf(el) === i)
}

export function uniqueBy<T>(arr: T[], keyExtractor: (el: T) => any): T[] {
  return arr
    .map(el => [el, keyExtractor(el)] as const)
    .filter(([_el, key], i, arr) => arr.findIndex(([_el, subKey]) => subKey === key) === i)
    .map(([el]) => el)
}

export function flatten(arr: any[]): any[] {
  return arr.reduce(function (flat, toFlatten) {
    return flat.concat(Array.isArray(toFlatten) ? flatten(toFlatten) : toFlatten)
  }, [])
}

export function chunk<T>(array: T[], chunkSize = 10) {
  const finalArray: T[][] = []
  const clonedArray = [...array]
  for (let i = 0; i < clonedArray.length; i += chunkSize) {
    const chunk = clonedArray.slice(i, i + chunkSize)
    finalArray.push(chunk)
  }
  return finalArray
}

export function sortBy<T>(arr: T[], fn: (ele: T) => any) {
  return arr
    .map((ele, index) => ({ ele, val: fn(ele), index }))
    .sort((a, b) =>
      a.val === b.val ? (a.index < b.index ? 1 : -1) : a.val < b.val ? -1 : a.val > b.val ? 1 : 0,
    )
    .map(({ ele }) => ele)
}

export function pushIfNeeded<T>(arr: T[], ...els: T[]): T[] {
  return els.reduce((acc, el) => (acc.includes(el) ? acc : [...acc, el]), arr)
}

export function removeElement<T>(arr: T[], el: T): T[] {
  return arr.filter(item => item !== el)
}

export function arrayToggleElement<T>(arr: T[], el: T, shouldAdd?: boolean): T[] {
  if (shouldAdd) {
    return pushIfNeeded(arr, el)
  } else if (shouldAdd === false) {
    return removeElement(arr, el)
  } else {
    return arr.includes(el) ? removeElement(arr, el) : pushIfNeeded(arr, el)
  }
}

export function includesDuplicate<T>(arr: T[], compare?: (a: T, b: T) => boolean) {
  compare ??= (a, b) => a === b
  return arr.some((el, i, arr) => arr.findIndex(el2 => compare?.(el, el2)) !== i)
}

export function filterDuplicates<T>(item: T, arr: T[], compare?: (a: T, b: T) => boolean) {
  compare ??= (a, b) => a === b
  return arr.filter(
    (el, i, arr) => compare?.(el, item) && i !== arr.findIndex(el2 => compare?.(item, el2)),
  )
}

export async function batch<T>(opts: {
  arr: T[]
  size: number
  fn: (batch: T[], batchIndex: number, totalBatches: number) => Promise<void>
}) {
  const { arr, size, fn } = opts

  for (let i = 0; i < arr.length; i += size) {
    const batch = arr.slice(i, i + size)
    await fn(batch, Math.floor(i / size), Math.ceil(arr.length / size))
  }
}
